/**
 * Miscellaneous graphics related utilities.
 * @module
 */
import type { CanvasBitmapSource } from "./CanvasBitmapSource";

function setCanvasSizeToMatchImageSource(canvas: HTMLCanvasElement, source: CanvasBitmapSource) {
  canvas.width = source.width;
  canvas.height = source.height;
}

export function canvasImageSourceToDataUrl(canvas: CanvasBitmapSource, type: string = "image/png") {
  return new Promise<string>((resolve, reject) => {
    // Check for toDataURL method directly instead of instanceof to support jsdom
    if (typeof (canvas as any).toDataURL === "function") {
      const dataURL = (canvas as any).toDataURL(type);
      if (dataURL) {
        resolve(dataURL);
        return;
      }
      reject();
    } else {
      const canvasElement = document.createElement("canvas");
      setCanvasSizeToMatchImageSource(canvasElement, canvas);
      const context = canvasElement.getContext("2d");
      if (context === null) {
        reject(new Error("Could not initialize canvas 2D context"));
        return;
      }
      context.drawImage(canvas, 0, 0);
      resolve(canvasElement.toDataURL(type));
    }
  });
}

export function canvasImageSourceToBlob(canvas: CanvasBitmapSource, type: string): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    // Check for toBlob method directly instead of instanceof to support jsdom
    if (typeof (canvas as any).toBlob === "function") {
      return (canvas as any).toBlob((blob: Blob | null) => {
        if (blob !== null) {
          resolve(blob);
          return;
        }
        reject();
      }, type);
    } else {
      const canvasElement = document.createElement("canvas");
      setCanvasSizeToMatchImageSource(canvasElement, canvas);
      const context = canvasElement.getContext("2d");
      if (context === null) {
        throw "Could not initialize canvas 2D context";
      }
      context.drawImage(canvas, 0, 0);
      return canvasElement.toBlob((blob) => {
        if (blob !== null) {
          resolve(blob);
          return;
        }
        reject();
      }, type);
    }
  });
}

export function canvasReadGrayscaleImageIntoArray(
  canvas: HTMLCanvasElement,
  image: HTMLImageElement | ImageBitmap,
): Uint8ClampedArray {
  canvas.width = image.width;
  canvas.height = image.height;
  const imageData = new Uint8ClampedArray(canvas.width * canvas.height);

  const context = canvas.getContext("2d", { willReadFrequently: false });
  if (context === null) {
    throw "Could not initialize canvas 2D context";
  }
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  const imageRGBA = context.getImageData(0, 0, canvas.width, canvas.height).data;

  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const grayscaleIndex = y * canvas.width + x;
      const rgbaIndex = y * (canvas.width * 4) + x * 4;
      imageData[grayscaleIndex] = imageRGBA[rgbaIndex];
    }
  }

  return imageData;
}

/**
 * Assumes a CSS transform without rotation or shear.
 * @param element
 * @returns The total CSS transform scale of the element and all its parents.
 */
export function getTotalCSSTransformScale(element: HTMLElement): { totalScaleX: number; totalScaleY: number } {
  let iteratedElement: HTMLElement | undefined = element;
  let totalScaleX = 1;
  let totalScaleY = 1;
  while (iteratedElement) {
    const style = window.getComputedStyle(iteratedElement);
    const transform = style.transform;
    if (transform && transform.startsWith("matrix")) {
      const values = transform.match(/matrix\s*\((.+)\)/)?.[1].split(", ");
      const scaleX = parseFloat(values?.[0] ?? "1");
      const scaleY = parseFloat(values?.[3] ?? "1");
      totalScaleX *= scaleX;
      totalScaleY *= scaleY;
    } else if (transform && transform.startsWith("scale")) {
      const values = transform.match(/scale\s*\((.+)\)/)?.[1].split(", ");
      const scaleX = parseFloat(values?.[0] ?? "1");
      const scaleY = parseFloat(values?.[1] ?? "1");
      totalScaleX *= scaleX;
      totalScaleY *= scaleY;
    }
    iteratedElement = iteratedElement.parentElement ?? undefined;
  }
  return { totalScaleX, totalScaleY };
}

/**
 * Load an image from a URL (including data URLs) into a canvas.
 * @param imageUrl The URL of the image to load
 * @returns Promise resolving to the canvas and its 2D context
 */
export async function loadImageToCanvas(imageUrl: string): Promise<{
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
}> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }

        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        resolve({ canvas, ctx });
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => reject(new Error(`Failed to load image: ${imageUrl}`));
    img.src = imageUrl;
  });
}
